// MultiCdmCaseBuilder.js
// -----------------------------------------------------------------------------
// Forked from rapidccs/RapidCaseList.js and reworked for multi-select.
//
// Differences from the Rapid selector it came from:
//   - picking is cumulative (2-13 cases), not one-and-done
//   - the order you pick in is the order you run in, and it's shown
//   - a random picker with filters that combine
//   - category names come from CASECATEGORYDESC on the merged row rather than a
//     hardcoded id->label map (the Rapid version's map didn't match the real
//     categories anyway)
//   - the session title and settings live here too
//
// Local UI state only. The case list and the create call belong to MultiCdmApp.
//
// Props:
//   cases          merged case rows                          (required)
//   loading        bool                                      (required)
//   error          string | null                             (required)
//   creating       bool — create call in flight              (required)
//   createError    string | null                             (optional)
//   initialSettings the user's last-used settings            (optional)
//   onCreate       ({ title, caseIds, settings }) => void    (required)
//   onCancel       () => void                                (required)
//   onRetry        () => void                                (optional)
// -----------------------------------------------------------------------------

import React from "react";
import MultiCdmSettingsPanel from "./MultiCdmSettingsPanel";
import {
  DEFAULT_SETTINGS,
  MAX_BLOCK_SIZE,
  MIN_BLOCK_SIZE,
  defaultSessionTitle,
  isCaseCompleted,
  pickRandomCases,
} from "./multiCdmFetches";

const SORTABLE = {
  ID: function (c) {
    return c.ID;
  },
  TITLE: function (c) {
    return (c.TITLE || "").toLowerCase();
  },
  CATEGORY: function (c) {
    return (c.CASECATEGORY || "").toLowerCase();
  },
  DIAGNOSIS: function (c) {
    return (c.DIAGNOSIS || "").toLowerCase();
  },
  COMPLETED: function (c) {
    return isCaseCompleted(c) ? 1 : 0;
  },
  AVG: function (c) {
    return typeof c.AvgGradeForCase === "number" ? c.AvgGradeForCase : -1;
  },
};

class MultiCdmCaseBuilder extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      // The block being built. Order matters — index 0 runs first.
      picked: [],
      title: "",
      titleTouched: false,
      // Seeded from the user's last-used settings (MultiCdmApp owns them and
      // persists them to the customer record). Local from here — the block gets
      // its own copy on create.
      settings: Object.assign({}, props.initialSettings || DEFAULT_SETTINGS),

      // Table controls
      search: "",
      sortKey: "ID",
      sortDir: "asc",
      showDiagnosis: false,
      hideTitle: false,
      hideCategory: false,
      hideCompleted: false,
      hideIncomplete: false,
      activeCats: [],

      // Random picker
      randomCount: 4,
      randomCompleted: true,
      randomIncomplete: true,
      randomCats: [],
      randomError: null,
    };
  }

  // ── Derived ──────────────────────────────────────────────────────────────

  // Demo cases never belong in a block.
  getSelectableCases() {
    return this.props.cases.filter(function (c) {
      return c.DEMO !== "Yes";
    });
  }

  getAllCategories() {
    const seen = {};
    this.getSelectableCases().forEach(function (c) {
      if (c.CASECATEGORY) seen[c.CASECATEGORY] = true;
    });
    return Object.keys(seen).sort();
  }

  getCaseById(caseId) {
    const all = this.props.cases;
    for (let i = 0; i < all.length; i++) {
      if (all[i].ID === caseId) return all[i];
    }
    return null;
  }

  pickIndex(caseId) {
    return this.state.picked.indexOf(caseId);
  }

  getVisible() {
    const {
      search,
      hideCompleted,
      hideIncomplete,
      activeCats,
      sortKey,
      sortDir,
    } = this.state;

    const term = search.trim().toLowerCase();
    let rows = this.getSelectableCases().filter(function (c) {
      if (term) {
        const hay =
          (c.DIAGNOSIS || "").toLowerCase() +
          " " +
          (c.TITLE || "").toLowerCase() +
          " " +
          String(c.ID);
        if (hay.indexOf(term) === -1) return false;
      }
      const completed = isCaseCompleted(c);
      if (hideCompleted && completed) return false;
      if (hideIncomplete && !completed) return false;
      if (
        activeCats.length > 0 &&
        activeCats.indexOf(c.CASECATEGORY) === -1
      ) {
        return false;
      }
      return true;
    });

    const getVal = SORTABLE[sortKey] || SORTABLE.ID;
    rows = rows.slice().sort(function (a, b) {
      const va = getVal(a);
      const vb = getVal(b);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return rows;
  }

  // The title box shows an auto-generated name until the user types their own,
  // then it's theirs and we stop rewriting it.
  effectiveTitle() {
    if (this.state.titleTouched) return this.state.title;
    return defaultSessionTitle(this.state.picked.length || 0);
  }

  // ── Handlers ─────────────────────────────────────────────────────────────

  toggleCase = (caseId) => {
    this.setState(function (prevState) {
      const at = prevState.picked.indexOf(caseId);
      if (at !== -1) {
        return {
          picked: prevState.picked.filter(function (id) {
            return id !== caseId;
          }),
          randomError: null,
        };
      }
      if (prevState.picked.length >= MAX_BLOCK_SIZE) {
        return {
          randomError:
            "A block holds at most " +
            MAX_BLOCK_SIZE +
            " cases, the same as a real exam day.",
        };
      }
      return {
        picked: prevState.picked.concat([caseId]),
        randomError: null,
      };
    });
  };

  clearPicked = () => {
    this.setState({ picked: [], randomError: null });
  };

  toggleCat = (name) => {
    this.setState(function (prevState) {
      const has = prevState.activeCats.indexOf(name) !== -1;
      return {
        activeCats: has
          ? prevState.activeCats.filter(function (x) {
              return x !== name;
            })
          : prevState.activeCats.concat([name]),
      };
    });
  };

  toggleRandomCat = (name) => {
    this.setState(function (prevState) {
      const has = prevState.randomCats.indexOf(name) !== -1;
      return {
        randomCats: has
          ? prevState.randomCats.filter(function (x) {
              return x !== name;
            })
          : prevState.randomCats.concat([name]),
        randomError: null,
      };
    });
  };

  setSort = (key) => {
    if (this.state.sortKey === key) {
      this.setState(function (prevState) {
        return { sortDir: prevState.sortDir === "asc" ? "desc" : "asc" };
      });
    } else {
      this.setState({ sortKey: key, sortDir: "asc" });
    }
  };

  sortArrow(key) {
    if (this.state.sortKey !== key) return null;
    return (
      <span className="mcdm-sort">
        {this.state.sortDir === "asc" ? "\u25b2" : "\u25bc"}
      </span>
    );
  }

  // Random replaces the current pick rather than adding to it — "give me 5
  // random Peds cases" means a block of 5, not 5 more.
  handleRandom = () => {
    const result = pickRandomCases(
      this.getSelectableCases(),
      this.state.randomCount,
      {
        includeCompleted: this.state.randomCompleted,
        includeIncomplete: this.state.randomIncomplete,
        categories: this.state.randomCats,
      },
    );

    if (result.error) {
      this.setState({ randomError: result.error });
      return;
    }
    this.setState({ picked: result.caseIds, randomError: null });
  };

  handleCreate = () => {
    const title = this.effectiveTitle().trim();
    // MultiCdmApp persists these as the user's new defaults on create.
    this.props.onCreate({
      title: title || defaultSessionTitle(this.state.picked.length),
      caseIds: this.state.picked,
      settings: this.state.settings,
    });
  };

  // ── Render ───────────────────────────────────────────────────────────────

  renderPicked() {
    const { picked, showDiagnosis, hideTitle } = this.state;

    if (picked.length === 0) {
      return (
        <p className="mcdm-hint">
          Nothing picked yet. Choose cases from the table, or draw some at
          random. They run in the order you pick them.
        </p>
      );
    }

    return (
      <div className="mcdm-picked">
        {picked.map((caseId, index) => {
          const row = this.getCaseById(caseId);

          // Mirror the table's display options so the two don't contradict
          // each other — hiding a title in the table but showing it here
          // would defeat the point of hiding it.
          const parts = [];
          if (row && !hideTitle) parts.push(row.TITLE);
          if (row && showDiagnosis) parts.push(row.DIAGNOSIS);

          return (
            <div className="mcdm-picked__row" key={caseId}>
              <span className="mcdm-order">{index + 1}</span>
              <span
                className="mcdm-picked__name"
                title={parts.length > 0 ? parts.join(" \u00b7 ") : undefined}
              >
                {caseId}
                {parts.length > 0 ? " \u00b7 " + parts.join(" \u00b7 ") : ""}
              </span>
              <button
                type="button"
                className="mcdm-picked__drop"
                aria-label={"Remove case " + caseId + " from the block"}
                onClick={() => {
                  this.toggleCase(caseId);
                }}
              >
                &times;
              </button>
            </div>
          );
        })}
      </div>
    );
  }

  render() {
    const { loading, error, onRetry, onCancel, creating, createError } =
      this.props;

    if (loading) {
      return (
        <div className="mcdm-scroll">
          <div className="mcdm-page">
            <div className="mcdm-loading">Loading cases...</div>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="mcdm-scroll">
          <div className="mcdm-page">
            <div className="mcdm-error">
              <p>{error}</p>
              {onRetry && (
                <button type="button" className="mcdm-btn" onClick={onRetry}>
                  Try again
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    const {
      picked,
      search,
      showDiagnosis,
      hideTitle,
      hideCategory,
      hideCompleted,
      hideIncomplete,
      activeCats,
      randomCount,
      randomCompleted,
      randomIncomplete,
      randomCats,
      randomError,
      settings,
    } = this.state;

    const categories = this.getAllCategories();
    const visible = this.getVisible();
    const canCreate =
      picked.length >= MIN_BLOCK_SIZE &&
      picked.length <= MAX_BLOCK_SIZE &&
      !creating;

    return (
      <div className="mcdm-builder-scroll">
        <div className="mcdm-builder-page">
          <div className="mcdm-head">
            <h1 className="mcdm-title">
              <small>Multi CDM</small>
              Build a block
            </h1>
            <div className="mcdm-head-actions">
              <button
                type="button"
                className="mcdm-btn"
                onClick={onCancel}
                disabled={creating}
              >
                Cancel
              </button>
              <button
                type="button"
                className="mcdm-btn mcdm-btn--primary"
                onClick={this.handleCreate}
                disabled={!canCreate}
              >
                {creating
                  ? "Starting..."
                  : "Start block" +
                    (picked.length > 0 ? " (" + picked.length + ")" : "")}
              </button>
            </div>
          </div>

          <div className="mcdm-builder-grid">
            {/* ── Case table ───────────────────────────────────────────── */}
            <div className="mcdm-builder-main">
              <div className="mcdm-search">
                <span className="mcdm-search__icon" aria-hidden="true">
                  &#8981;
                </span>
                <input
                  type="text"
                  placeholder="Search by diagnosis, title, or case number"
                  aria-label="Search cases"
                  value={search}
                  onChange={(e) => {
                    this.setState({ search: e.target.value });
                  }}
                />
              </div>

              <div
                className="mcdm-chips"
                role="group"
                aria-label="Filter by category"
              >
                {categories.map((name) => {
                  const on = activeCats.indexOf(name) !== -1;
                  return (
                    <button
                      key={name}
                      type="button"
                      className={"mcdm-chip" + (on ? " mcdm-chip--on" : "")}
                      aria-pressed={on}
                      onClick={() => {
                        this.toggleCat(name);
                      }}
                    >
                      {name}
                    </button>
                  );
                })}
              </div>

              <div
                className="mcdm-chips"
                role="group"
                aria-label="Display options"
              >
                <label className="mcdm-check">
                  <input
                    type="checkbox"
                    checked={showDiagnosis}
                    onChange={(e) => {
                      this.setState({ showDiagnosis: e.target.checked });
                    }}
                  />
                  Show diagnosis
                </label>
                <label className="mcdm-check">
                  <input
                    type="checkbox"
                    checked={hideTitle}
                    onChange={(e) => {
                      this.setState({ hideTitle: e.target.checked });
                    }}
                  />
                  Hide title
                </label>
                <label className="mcdm-check">
                  <input
                    type="checkbox"
                    checked={hideCategory}
                    onChange={(e) => {
                      this.setState({ hideCategory: e.target.checked });
                    }}
                  />
                  Hide category
                </label>
                <label className="mcdm-check">
                  <input
                    type="checkbox"
                    checked={hideCompleted}
                    onChange={(e) => {
                      this.setState({
                        hideCompleted: e.target.checked,
                        hideIncomplete: e.target.checked
                          ? false
                          : hideIncomplete,
                      });
                    }}
                  />
                  Hide completed
                </label>
                <label className="mcdm-check">
                  <input
                    type="checkbox"
                    checked={hideIncomplete}
                    onChange={(e) => {
                      this.setState({
                        hideIncomplete: e.target.checked,
                        hideCompleted: e.target.checked ? false : hideCompleted,
                      });
                    }}
                  />
                  Hide incomplete
                </label>
              </div>

              <p className="mcdm-count">
                Showing {visible.length} of {this.getSelectableCases().length}{" "}
                cases
              </p>

              <div className="mcdm-sort-mobile">
                <select
                  className="mcdm-select"
                  aria-label="Sort cases by"
                  value={this.state.sortKey}
                  onChange={(e) => {
                    this.setState({ sortKey: e.target.value, sortDir: "asc" });
                  }}
                >
                  <option value="ID">Sort: Case</option>
                  {!hideTitle && <option value="TITLE">Sort: Title</option>}
                  {!hideCategory && (
                    <option value="CATEGORY">Sort: Category</option>
                  )}
                  {showDiagnosis && (
                    <option value="DIAGNOSIS">Sort: Diagnosis</option>
                  )}
                  <option value="COMPLETED">Sort: Status</option>
                  <option value="AVG">Sort: Avg</option>
                </select>
                <button
                  type="button"
                  className="mcdm-btn mcdm-sort-mobile__dir"
                  aria-label={
                    this.state.sortDir === "asc"
                      ? "Sorting ascending, tap to sort descending"
                      : "Sorting descending, tap to sort ascending"
                  }
                  onClick={() => {
                    this.setState(function (prevState) {
                      return {
                        sortDir: prevState.sortDir === "asc" ? "desc" : "asc",
                      };
                    });
                  }}
                >
                  {this.state.sortDir === "asc" ? "\u25b2" : "\u25bc"}
                </button>
              </div>

              <div className="mcdm-table-wrap">
                <table className="mcdm-table">
                  <thead>
                    <tr>
                      <th aria-label="Order in block" />
                      <th
                        onClick={() => {
                          this.setSort("ID");
                        }}
                      >
                        Case {this.sortArrow("ID")}
                      </th>
                      {!hideTitle && (
                        <th
                          onClick={() => {
                            this.setSort("TITLE");
                          }}
                        >
                          Title {this.sortArrow("TITLE")}
                        </th>
                      )}
                      {!hideCategory && (
                        <th
                          onClick={() => {
                            this.setSort("CATEGORY");
                          }}
                        >
                          Category {this.sortArrow("CATEGORY")}
                        </th>
                      )}
                      {showDiagnosis && (
                        <th
                          onClick={() => {
                            this.setSort("DIAGNOSIS");
                          }}
                        >
                          Diagnosis {this.sortArrow("DIAGNOSIS")}
                        </th>
                      )}
                      <th
                        onClick={() => {
                          this.setSort("COMPLETED");
                        }}
                      >
                        Status {this.sortArrow("COMPLETED")}
                      </th>
                      <th
                        onClick={() => {
                          this.setSort("AVG");
                        }}
                      >
                        Avg {this.sortArrow("AVG")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.length === 0 && (
                      <tr>
                        <td className="mcdm-empty-row" colSpan={7}>
                          No cases match those filters.
                        </td>
                      </tr>
                    )}
                    {visible.map((c) => {
                      const at = this.pickIndex(c.ID);
                      const on = at !== -1;
                      return (
                        <tr
                          key={c.ID}
                          className={on ? "mcdm-row--on" : ""}
                          onClick={() => {
                            this.toggleCase(c.ID);
                          }}
                        >
                          <td>
                            <span
                              className={
                                "mcdm-order" + (on ? "" : " mcdm-order--empty")
                              }
                              aria-label={
                                on
                                  ? "Case " + c.ID + " runs " + (at + 1) + " in the block"
                                  : "Case " + c.ID + " is not in the block"
                              }
                            >
                              {on ? at + 1 : "\u00b7"}
                            </span>
                          </td>
                          <td className="mcdm-id" data-label="Case">
                            {c.ID}
                          </td>
                          {!hideTitle && (
                            <td data-label="Title">{c.TITLE}</td>
                          )}
                          {!hideCategory && (
                            <td data-label="Category">
                              {c.CASECATEGORY}
                            </td>
                          )}
                          {showDiagnosis && (
                            <td className="mcdm-diag" data-label="Diagnosis">
                              {c.DIAGNOSIS}
                            </td>
                          )}
                          <td data-label="Status">
                            {isCaseCompleted(c) ? (
                              <span className="mcdm-pill mcdm-pill--done">
                                Completed
                              </span>
                            ) : (
                              <span className="mcdm-pill mcdm-pill--todo">
                                Not started
                              </span>
                            )}
                          </td>
                          <td className="mcdm-grade" data-label="Avg">
                            {isCaseCompleted(c) &&
                            typeof c.AvgGradeForCase === "number"
                              ? c.AvgGradeForCase.toFixed(1) + "%"
                              : "\u2014"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Sidebar ──────────────────────────────────────────────── */}
            <div className="mcdm-builder-side">
              <div className="mcdm-panel">
                <h2 className="mcdm-panel__title">
                  Your block &mdash; {picked.length} of {MAX_BLOCK_SIZE}
                </h2>
                {this.renderPicked()}
                {picked.length > 0 && (
                  <button
                    type="button"
                    className="mcdm-btn mcdm-btn--ghost"
                    onClick={this.clearPicked}
                  >
                    Clear all
                  </button>
                )}
                {picked.length > 0 && picked.length < MIN_BLOCK_SIZE && (
                  <p className="mcdm-hint">
                    Add {MIN_BLOCK_SIZE - picked.length} more to start a block.
                    For a single case, use the normal case list.
                  </p>
                )}
                {randomError && (
                  <p className="mcdm-inline-error">{randomError}</p>
                )}
              </div>

              <div className="mcdm-panel">
                <h2 className="mcdm-panel__title">Name</h2>
                <div className="mcdm-field">
                  <input
                    type="text"
                    className="mcdm-input"
                    maxLength={120}
                    aria-label="Block name"
                    value={this.effectiveTitle()}
                    onChange={(e) => {
                      this.setState({
                        title: e.target.value,
                        titleTouched: true,
                      });
                    }}
                  />
                  <p className="mcdm-hint">You can rename this later.</p>
                </div>
              </div>

              <div className="mcdm-panel">
                <h2 className="mcdm-panel__title">Draw at random</h2>
                <div className="mcdm-field">
                  <label className="mcdm-label" htmlFor="mcdm-random-count">
                    How many
                  </label>
                  <select
                    id="mcdm-random-count"
                    className="mcdm-select"
                    value={String(randomCount)}
                    onChange={(e) => {
                      this.setState({
                        randomCount: parseInt(e.target.value, 10),
                        randomError: null,
                      });
                    }}
                  >
                    {Array.from(
                      { length: MAX_BLOCK_SIZE - MIN_BLOCK_SIZE + 1 },
                      function (_, i) {
                        return MIN_BLOCK_SIZE + i;
                      },
                    ).map(function (n) {
                      return (
                        <option key={n} value={String(n)}>
                          {n} cases
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="mcdm-field">
                  <span className="mcdm-label">Draw from</span>
                  <label className="mcdm-check">
                    <input
                      type="checkbox"
                      checked={randomIncomplete}
                      onChange={(e) => {
                        this.setState({
                          randomIncomplete: e.target.checked,
                          randomError: null,
                        });
                      }}
                    />
                    Cases you haven't done
                  </label>
                  <label className="mcdm-check">
                    <input
                      type="checkbox"
                      checked={randomCompleted}
                      onChange={(e) => {
                        this.setState({
                          randomCompleted: e.target.checked,
                          randomError: null,
                        });
                      }}
                    />
                    Cases you've completed
                  </label>
                </div>

                <div className="mcdm-field">
                  <span className="mcdm-label">Categories</span>
                  <div className="mcdm-chips">
                    {categories.map((name) => {
                      const on = randomCats.indexOf(name) !== -1;
                      return (
                        <button
                          key={name}
                          type="button"
                          className={"mcdm-chip" + (on ? " mcdm-chip--on" : "")}
                          aria-pressed={on}
                          onClick={() => {
                            this.toggleRandomCat(name);
                          }}
                        >
                          {name}
                        </button>
                      );
                    })}
                  </div>
                  <p className="mcdm-hint">
                    {randomCats.length === 0
                      ? "All categories."
                      : "Only the categories you picked."}
                  </p>
                </div>

                <button
                  type="button"
                  className="mcdm-btn mcdm-btn--accent"
                  onClick={this.handleRandom}
                  disabled={!randomCompleted && !randomIncomplete}
                >
                  Draw {randomCount} cases
                </button>
                <p className="mcdm-hint">
                  This replaces whatever you've picked so far, in a random
                  order.
                </p>
              </div>

              <div className="mcdm-panel">
                <h2 className="mcdm-panel__title">Settings</h2>
                <MultiCdmSettingsPanel
                  settings={settings}
                  disabled={creating}
                  onChange={(next) => {
                    this.setState({ settings: next });
                  }}
                />
              </div>

              {createError && <p className="mcdm-inline-error">{createError}</p>}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default MultiCdmCaseBuilder;